/**
 * RDM seed content for the flowchart native type. Authored as `process`
 * RDM so RdmService.toLayoutedRuntimeModel runs ELK layout (it only lays
 * out `process` | `bpmn`). Swimlane + BPMN are flowchart-domain CONTENT,
 * so they live here as seeds on the same native type — not separate types.
 *
 * Node types drive the rendered shape: flowchart shapes use `process` /
 * `decision` / `start-end`; BPMN shapes use the BPMN vocabulary
 * (`start-event` / `end-event` / `user-task` / `service-task` /
 * `exclusive-gateway`) — same types the process-capture extractor emits
 * (see PipelineConfig.stepExtraction). Don't mix flowchart node types into
 * the BPMN seed or you get flowchart rectangles in a BPMN diagram.
 *
 * Keep these minimal and recognizable: the activation goal is "edit
 * familiar content in <60s", not an exhaustive diagram.
 */

import type { Edge, Node } from '../../models/scene';
import { applyProcessEdgeRouteUpdates, ProcessEdgeRoutingService } from '../../../domains/process/services/ProcessEdgeRoutingService';
import type { RdmLayoutOptions } from '../../../domains/rdm/services/RdmLayoutService';
import type { SeedProvider } from '../SeedRegistry';

export const FLOWCHART_BASIC_RDM = `---
type: process
title: "Flowchart"
---
process Flowchart {
  start_1 { type: "start-end", label: "Start", color: "green" }
  step_1 { type: "process", label: "Do the work", color: "blue" }
  decide_1 { type: "decision", label: "Looks good?", color: "amber" }
  step_2 { type: "process", label: "Make changes", color: "blue" }
  end_1 { type: "start-end", label: "Done", color: "green" }
  start_1 -> step_1
  step_1 -> decide_1
  decide_1 -> end_1 { label: "Yes" }
  decide_1 -> step_2 { label: "No" }
  step_2 -> end_1
}
`;

const SWIMLANE_APPROVAL_RDM = `---
type: process
title: "Approval workflow"
pool: lane
---
process Approval {
  start_1 { kind: "start", label: "Request submitted", lane: "Requester", type: "start-end", color: "green" }
  review { kind: "process", label: "Review request", lane: "Manager", stepKind: "user_task", type: "process", color: "blue" }
  decide { kind: "decision", label: "Approve?", lane: "Manager", type: "gateway", color: "amber" }
  notify_ok { kind: "process", label: "Approve & notify", lane: "Manager", stepKind: "service_task", type: "process", color: "green" }
  notify_no { kind: "process", label: "Reject & notify", lane: "Manager", stepKind: "service_task", type: "process", color: "red" }
  receive { kind: "process", label: "Receive outcome", lane: "Requester", type: "process", color: "blue" }
  end_1 { kind: "end", label: "Done", lane: "Requester", type: "start-end", color: "green" }
  start_1 -> review
  review -> decide
  decide -> notify_ok { label: "Yes" }
  decide -> notify_no { label: "No" }
  notify_ok -> receive
  notify_no -> receive
  receive -> end_1
}
`;

const BPMN_ORDER_RDM = `---
type: process
title: "Order fulfillment"
pool: lane
---
process OrderFulfillment {
  start_1 { label: "Order received", lane: "Sales", type: "start-event", color: "green" }
  validate { label: "Validate order", lane: "Sales", type: "user-task", color: "blue" }
  in_stock { label: "In stock?", lane: "Warehouse", type: "exclusive-gateway", color: "amber" }
  pick { label: "Pick & pack", lane: "Warehouse", type: "service-task", color: "blue" }
  backorder { label: "Create backorder", lane: "Warehouse", type: "service-task", color: "orange" }
  ship { label: "Ship order", lane: "Logistics", type: "service-task", color: "blue" }
  end_1 { label: "Order complete", lane: "Logistics", type: "end-event", color: "green" }
  start_1 -> validate
  validate -> in_stock
  in_stock -> pick { label: "Yes" }
  in_stock -> backorder { label: "No" }
  pick -> ship
  backorder -> ship
  ship -> end_1
}
`;

const NEW_HIRE_ONBOARDING_RDM = `---
type: process
title: "New-hire onboarding SOP"
pool: lane
---
process NewHireOnboarding {
  start_1 { label: "Offer accepted", lane: "HR", type: "start-end", color: "green" }
  paperwork { label: "Contract & payroll setup", lane: "HR", type: "process", color: "blue" }
  accounts { label: "Provision accounts & laptop", lane: "IT", type: "process", color: "blue" }
  ready { label: "Equipment ready?", lane: "IT", type: "decision", color: "amber" }
  escalate { label: "Escalate to IT lead", lane: "IT", type: "process", color: "red" }
  plan_30 { label: "Share 30-60-90 plan", lane: "Manager", type: "process", color: "blue" }
  buddy { label: "Assign onboarding buddy", lane: "Manager", type: "process", color: "blue" }
  first_day { label: "First-day orientation", lane: "New hire", type: "process", color: "blue" }
  end_1 { label: "Onboarded", lane: "New hire", type: "start-end", color: "green" }
  start_1 -> paperwork
  paperwork -> accounts
  accounts -> ready
  ready -> plan_30 { label: "Yes" }
  ready -> escalate { label: "No" }
  escalate -> plan_30
  plan_30 -> buddy
  buddy -> first_day
  first_day -> end_1
}
`;

const INCIDENT_POSTMORTEM_RDM = `---
type: process
title: "Incident postmortem"
pool: lane
---
process IncidentPostmortem {
  alert { label: "Alert fired 02:14", lane: "Timeline", type: "start-end", color: "red" }
  triage { label: "On-call paged & triage", lane: "Timeline", type: "process", color: "blue" }
  mitigate { label: "Mitigation: rollback deploy", lane: "Timeline", type: "process", color: "blue" }
  restored { label: "Service restored 03:02", lane: "Timeline", type: "start-end", color: "green" }
  why_1 { label: "Why? Deploy had no canary stage", lane: "Five whys", type: "process", color: "amber" }
  why_2 { label: "Why? Pipeline skips staged rollout", lane: "Five whys", type: "process", color: "amber" }
  root { label: "Root cause: rollout policy gap", lane: "Five whys", type: "decision", color: "red" }
  action_1 { label: "Add canary stage to deploy", lane: "Actions", type: "process", color: "green" }
  action_2 { label: "Alert on error-budget burn", lane: "Actions", type: "process", color: "green" }
  alert -> triage
  triage -> mitigate
  mitigate -> restored
  restored -> why_1
  why_1 -> why_2
  why_2 -> root
  root -> action_1
  root -> action_2
}
`;

const CARE_PATHWAY_RDM = `---
type: process
title: "Care pathway"
pool: lane
---
process CarePathway {
  referral { label: "Referral received", lane: "Intake", type: "start-end", color: "green" }
  screen { label: "Initial screening call", lane: "Intake", type: "process", color: "blue" }
  assess { label: "Comprehensive assessment", lane: "Clinician", type: "process", color: "blue" }
  eligible { label: "Meets service criteria?", lane: "Clinician", type: "decision", color: "amber" }
  refer_out { label: "Refer to partner service", lane: "Clinician", type: "process", color: "orange" }
  plan { label: "Develop care plan", lane: "Clinician", type: "process", color: "blue" }
  deliver { label: "Deliver sessions", lane: "Care team", type: "process", color: "blue" }
  review { label: "90-day outcome review", lane: "Care team", type: "process", color: "blue" }
  end_1 { label: "Discharge / step-down", lane: "Care team", type: "start-end", color: "green" }
  referral -> screen
  screen -> assess
  assess -> eligible
  eligible -> plan { label: "Yes" }
  eligible -> refer_out { label: "No" }
  plan -> deliver
  deliver -> review
  review -> end_1
}
`;

const DECISION_FLOW_RDM = `---
type: process
title: "Decision flow"
---
process DecisionFlow {
  start_step { type: "start-end", label: "Trigger", color: "green" }
  qualify { type: "process", label: "Gather context", color: "blue" }
  decision_step { type: "decision", label: "Which path?", color: "amber" }
  path_a { type: "process", label: "Path A action", color: "blue" }
  path_b { type: "process", label: "Path B action", color: "orange" }
  review { type: "process", label: "Review outcome", color: "blue" }
  done { type: "start-end", label: "Close", color: "green" }
  start_step -> qualify
  qualify -> decision_step
  decision_step -> path_a { label: "A" }
  decision_step -> path_b { label: "B" }
  path_a -> review
  path_b -> review
  review -> done
}
`;

const STANDARD_OPERATING_PROCEDURE_RDM = `---
type: process
title: "Standard operating procedure"
pool: lane
---
process StandardOperatingProcedure {
  start_step { label: "Need identified", lane: "Requester", type: "start-end", color: "green" }
  prepare { label: "Prepare request", lane: "Requester", type: "process", color: "blue" }
  perform { label: "Perform procedure", lane: "Operator", type: "process", color: "blue" }
  check { label: "Quality check", lane: "Reviewer", type: "decision", color: "amber" }
  rework { label: "Rework step", lane: "Operator", type: "process", color: "orange" }
  approve { label: "Approve record", lane: "Reviewer", type: "process", color: "green" }
  archive { label: "Archive evidence", lane: "System", type: "process", color: "blue" }
  end_step { label: "Procedure complete", lane: "Requester", type: "start-end", color: "green" }
  start_step -> prepare
  prepare -> perform
  perform -> check
  check -> approve { label: "Pass" }
  check -> rework { label: "Rework" }
  rework -> check
  approve -> archive
  archive -> end_step
}
`;

export const HANDOFF_PROCESS_RDM = `---
type: process
title: "Team handoff process"
pool: lane
---
process TeamHandoff {
  intake { label: "Work ready to hand off", lane: "Team A", type: "start-end", color: "green" }
  package { label: "Package context", lane: "Team A", type: "process", color: "blue" }
  accept { label: "Accept handoff?", lane: "Team B", type: "decision", color: "amber" }
  clarify { label: "Request clarification", lane: "Team B", type: "process", color: "orange" }
  resolve { label: "Resolve questions", lane: "Team A", type: "process", color: "blue" }
  execute { label: "Continue work", lane: "Team B", type: "process", color: "blue" }
  notify { label: "Notify stakeholder", lane: "Customer / Stakeholder", type: "process", color: "green" }
  done { label: "Handoff complete", lane: "Team B", type: "start-end", color: "green" }
  intake -> package
  package -> accept
  accept -> execute { label: "Yes" }
  accept -> clarify { label: "Need info" }
  clarify -> resolve
  resolve -> accept
  execute -> notify
  notify -> done
}
`;

const PROCESS_TEMPLATE_SEED_NUDGES: Readonly<Record<string, Readonly<Record<string, { dx?: number; dy?: number }>>>> = {
  'flowchart-basic': {
    step_2: { dx: 40 },
  },
  'handoff-process': {
    execute: { dy: -20 },
    clarify: { dy: 20 },
  },
};

export function applyProcessTemplateSeedNudges(seedId: string, nodes: Node[], edges: Edge[]): void {
  const nudges = PROCESS_TEMPLATE_SEED_NUDGES[seedId];
  if (!nudges) return;

  for (const node of nodes) {
    const nudge = nudges[node.id];
    if (!nudge) continue;
    if (typeof nudge.dx === 'number') node.x = (node.x ?? 0) + nudge.dx;
    if (typeof nudge.dy === 'number') node.y = (node.y ?? 0) + nudge.dy;
  }

  refreshSwimlaneTableDockOffsets(nodes);

  const routingService = new ProcessEdgeRoutingService();
  const routeUpdates = routingService.route(nodes, edges, {
    layoutSource: nodes.some((node) => node.attrs?.__isPoolTable) ? 'process-swimlane' : 'elk',
  });
  applyProcessEdgeRouteUpdates(edges, routeUpdates);
}

function refreshSwimlaneTableDockOffsets(nodes: Node[]): void {
  const poolTable = nodes.find((node) => node.attrs?.__isPoolTable);
  if (!poolTable) return;

  const laneBounds = poolTable.attrs?.laneBounds;
  if (!Array.isArray(laneBounds)) return;

  const laneDirection = poolTable.attrs?.laneDirection === 'vertical' ? 'vertical' : 'horizontal';
  const headerSize = 50;

  for (const node of nodes) {
    if (node.attrs?.__isPoolTable) continue;

    const laneIndex = typeof node.attrs?.__laneIndex === 'number' ? node.attrs.__laneIndex : null;
    if (laneIndex == null) continue;

    const lane = laneBounds[laneIndex] as { start?: number } | undefined;
    if (!lane || typeof lane.start !== 'number') continue;

    const cellX = laneDirection === 'horizontal'
      ? (poolTable.x ?? 0) + headerSize
      : (poolTable.x ?? 0) + lane.start;
    const cellY = laneDirection === 'horizontal'
      ? (poolTable.y ?? 0) + lane.start
      : (poolTable.y ?? 0) + headerSize;

    node.attrs = {
      ...(node.attrs ?? {}),
      tableDock: {
        tableId: poolTable.id,
        cellKey: laneDirection === 'horizontal' ? `${laneIndex},1` : `0,${laneIndex + 1}`,
        offset: {
          x: (node.x ?? 0) - cellX,
          y: (node.y ?? 0) - cellY,
        },
      },
    };
  }
}

const BPMN_EXCEPTION_HANDLING_RDM = `---
type: process
title: "BPMN exception handling"
pool: lane
---
process ExceptionHandling {
  start_request { label: "Request received", lane: "Operations", type: "start-event", color: "green" }
  validate { label: "Validate request", lane: "Operations", type: "user-task", color: "blue" }
  valid { label: "Valid?", lane: "Operations", type: "exclusive-gateway", color: "amber" }
  fulfill { label: "Fulfill request", lane: "Service team", type: "service-task", color: "blue" }
  exception { label: "Create exception case", lane: "Service team", type: "user-task", color: "orange" }
  approve { label: "Approve exception?", lane: "Manager", type: "exclusive-gateway", color: "amber" }
  reject { label: "Notify rejection", lane: "Service team", type: "service-task", color: "red" }
  close_case { label: "Close case", lane: "Operations", type: "end-event", color: "green" }
  start_request -> validate
  validate -> valid
  valid -> fulfill { label: "Yes" }
  valid -> exception { label: "No" }
  exception -> approve
  approve -> fulfill { label: "Approved" }
  approve -> reject { label: "Rejected" }
  fulfill -> close_case
  reject -> close_case
}
`;

const BPMN_POOL_COLLABORATION_RDM = `---
type: process
title: "BPMN collaboration"
pool: lane
---
process Collaboration {
  customer_start { label: "Customer submits request", lane: "Customer", type: "start-event", color: "green" }
  support_triage { label: "Triage request", lane: "Support", type: "user-task", color: "blue" }
  needs_billing { label: "Billing issue?", lane: "Support", type: "exclusive-gateway", color: "amber" }
  billing_review { label: "Review account", lane: "Billing", type: "user-task", color: "blue" }
  support_resolve { label: "Resolve case", lane: "Support", type: "service-task", color: "blue" }
  customer_confirm { label: "Confirm resolution", lane: "Customer", type: "user-task", color: "green" }
  end_process { label: "Case closed", lane: "Support", type: "end-event", color: "green" }
  customer_start -> support_triage
  support_triage -> needs_billing
  needs_billing -> billing_review { label: "Yes" }
  needs_billing -> support_resolve { label: "No" }
  billing_review -> support_resolve
  support_resolve -> customer_confirm
  customer_confirm -> end_process
}
`;

const SERVICE_BLUEPRINT_RDM = `---
type: process
title: "Service blueprint"
pool: lane
---
process ServiceBlueprint {
  discover { label: "Discover service", lane: "Customer actions", type: "start-end", color: "green" }
  request_help { label: "Request help", lane: "Customer actions", type: "process", color: "blue" }
  greet { label: "Acknowledge request", lane: "Frontstage", type: "process", color: "blue" }
  diagnose { label: "Diagnose need", lane: "Frontstage", type: "decision", color: "amber" }
  prepare { label: "Prepare resolution", lane: "Backstage", type: "process", color: "blue" }
  update_record { label: "Update service record", lane: "Support processes", type: "process", color: "blue" }
  deliver { label: "Deliver outcome", lane: "Frontstage", type: "process", color: "green" }
  evidence { label: "Receipt / confirmation", lane: "Physical evidence", type: "process", color: "green" }
  end_process { label: "Need resolved", lane: "Customer actions", type: "start-end", color: "green" }
  discover -> request_help
  request_help -> greet
  greet -> diagnose
  diagnose -> prepare
  prepare -> update_record
  update_record -> deliver
  deliver -> evidence
  evidence -> end_process
}
`;

const CUSTOMER_JOURNEY_PROCESS_RDM = `---
type: process
title: "Customer journey to process"
pool: lane
---
process CustomerJourneyProcess {
  aware { label: "Customer becomes aware", lane: "Journey stage", type: "start-end", color: "green" }
  compare { label: "Compare options", lane: "Journey stage", type: "process", color: "blue" }
  onboard { label: "Onboard customer", lane: "Frontstage team", type: "process", color: "blue" }
  setup { label: "Configure account", lane: "Backstage team", type: "process", color: "blue" }
  risk { label: "Friction or risk?", lane: "Service owner", type: "decision", color: "amber" }
  improve { label: "Launch improvement", lane: "Service owner", type: "process", color: "orange" }
  adopt { label: "Customer adopts", lane: "Journey stage", type: "process", color: "green" }
  renew { label: "Renew / expand", lane: "Journey stage", type: "start-end", color: "green" }
  aware -> compare
  compare -> onboard
  onboard -> setup
  setup -> risk
  risk -> adopt { label: "No" }
  risk -> improve { label: "Yes" }
  improve -> adopt
  adopt -> renew
}
`;

const SUPPORT_MODEL_RDM = `---
type: process
title: "Support model"
pool: lane
---
process SupportModel {
  ticket { label: "Ticket opened", lane: "Customer", type: "start-end", color: "green" }
  triage { label: "Triage severity", lane: "Tier 1", type: "decision", color: "amber" }
  resolve_l1 { label: "Resolve known issue", lane: "Tier 1", type: "process", color: "blue" }
  escalate { label: "Escalate with context", lane: "Tier 1", type: "process", color: "orange" }
  investigate { label: "Investigate root cause", lane: "Tier 2", type: "process", color: "blue" }
  fix { label: "Apply fix / workaround", lane: "Engineering", type: "process", color: "blue" }
  notify { label: "Notify customer", lane: "Tier 1", type: "process", color: "green" }
  close_ticket { label: "Close ticket", lane: "Customer", type: "start-end", color: "green" }
  ticket -> triage
  triage -> resolve_l1 { label: "Known" }
  triage -> escalate { label: "Complex" }
  escalate -> investigate
  investigate -> fix
  resolve_l1 -> notify
  fix -> notify
  notify -> close_ticket
}
`;

const SIPOC_RDM = `---
type: process
title: "SIPOC"
pool: lane
---
process Sipoc {
  supplier { label: "Supplier", lane: "Suppliers", type: "start-end", color: "green" }
  input { label: "Input", lane: "Inputs", type: "process", color: "blue" }
  process_step { label: "Core process", lane: "Process", type: "process", color: "blue" }
  output { label: "Output", lane: "Outputs", type: "process", color: "green" }
  customer { label: "Customer", lane: "Customers", type: "start-end", color: "green" }
  measure { label: "Measure quality", lane: "Process", type: "decision", color: "amber" }
  improve { label: "Improve input / process", lane: "Process", type: "process", color: "orange" }
  supplier -> input
  input -> process_step
  process_step -> output
  output -> customer
  output -> measure
  measure -> improve { label: "Gap" }
  improve -> process_step
}
`;

const VALUE_STREAM_MAP_RDM = `---
type: process
title: "Value stream map"
pool: lane
---
process ValueStreamMap {
  demand { label: "Customer demand", lane: "Information flow", type: "start-end", color: "green" }
  plan { label: "Plan work", lane: "Information flow", type: "process", color: "blue" }
  receive { label: "Receive request", lane: "Material / work flow", type: "process", color: "blue" }
  process_a { label: "Process step A", lane: "Material / work flow", type: "process", color: "blue" }
  wait { label: "Queue / wait", lane: "Waste signals", type: "process", color: "orange" }
  process_b { label: "Process step B", lane: "Material / work flow", type: "process", color: "blue" }
  ship { label: "Deliver value", lane: "Material / work flow", type: "start-end", color: "green" }
  improve { label: "Kaizen opportunity", lane: "Waste signals", type: "decision", color: "amber" }
  demand -> plan
  plan -> receive
  receive -> process_a
  process_a -> wait
  wait -> process_b
  process_b -> ship
  wait -> improve
  improve -> process_a { label: "Reduce" }
}
`;

const BOTTLENECK_MAP_RDM = `---
type: process
title: "Bottleneck map"
pool: lane
---
process BottleneckMap {
  start_flow { label: "Work enters", lane: "Flow", type: "start-end", color: "green" }
  step_a { label: "Step A", lane: "Flow", type: "process", color: "blue" }
  bottleneck { label: "Bottleneck / wait", lane: "Flow", type: "decision", color: "red" }
  expedite { label: "Expedite critical work", lane: "Countermeasure", type: "process", color: "orange" }
  rebalance { label: "Rebalance capacity", lane: "Countermeasure", type: "process", color: "blue" }
  step_b { label: "Step B", lane: "Flow", type: "process", color: "blue" }
  end_flow { label: "Work complete", lane: "Flow", type: "start-end", color: "green" }
  start_flow -> step_a
  step_a -> bottleneck
  bottleneck -> step_b { label: "Normal" }
  bottleneck -> expedite { label: "Blocked" }
  expedite -> rebalance
  rebalance -> step_b
  step_b -> end_flow
}
`;

const FIVE_WHYS_RDM = `---
type: process
title: "5 Whys root cause"
pool: lane
---
process FiveWhys {
  problem { label: "Problem observed", lane: "Problem", type: "start-end", color: "red" }
  why1 { label: "Why 1?", lane: "Analysis", type: "process", color: "amber" }
  why2 { label: "Why 2?", lane: "Analysis", type: "process", color: "amber" }
  why3 { label: "Why 3?", lane: "Analysis", type: "process", color: "amber" }
  root { label: "Likely root cause", lane: "Analysis", type: "decision", color: "red" }
  action { label: "Corrective action", lane: "Actions", type: "process", color: "green" }
  verify { label: "Verify effectiveness", lane: "Actions", type: "process", color: "blue" }
  close_analysis { label: "Learning captured", lane: "Actions", type: "start-end", color: "green" }
  problem -> why1
  why1 -> why2
  why2 -> why3
  why3 -> root
  root -> action
  action -> verify
  verify -> close_analysis
}
`;

const CHANGE_CONTROL_RDM = `---
type: process
title: "Change enablement"
pool: lane
---
process ChangeEnablement {
  request_change { label: "Change requested", lane: "Requester", type: "start-end", color: "green" }
  assess { label: "Assess risk and impact", lane: "Change manager", type: "process", color: "blue" }
  cab { label: "CAB review needed?", lane: "Change manager", type: "decision", color: "amber" }
  approve { label: "Approve change", lane: "Approver", type: "process", color: "green" }
  schedule { label: "Schedule window", lane: "Implementation team", type: "process", color: "blue" }
  implement { label: "Implement change", lane: "Implementation team", type: "process", color: "blue" }
  review { label: "Post-implementation review", lane: "Change manager", type: "process", color: "blue" }
  close_change { label: "Change closed", lane: "Requester", type: "start-end", color: "green" }
  request_change -> assess
  assess -> cab
  cab -> approve { label: "Yes" }
  cab -> schedule { label: "Standard" }
  approve -> schedule
  schedule -> implement
  implement -> review
  review -> close_change
}
`;

const INCIDENT_MANAGEMENT_RDM = `---
type: process
title: "Incident management"
pool: lane
---
process IncidentManagement {
  detect { label: "Incident detected", lane: "Monitoring / User", type: "start-end", color: "red" }
  log { label: "Log and categorize", lane: "Service desk", type: "process", color: "blue" }
  major { label: "Major incident?", lane: "Service desk", type: "decision", color: "amber" }
  bridge { label: "Open incident bridge", lane: "Incident commander", type: "process", color: "red" }
  resolve { label: "Resolve or workaround", lane: "Resolver group", type: "process", color: "blue" }
  communicate { label: "Communicate status", lane: "Incident commander", type: "process", color: "blue" }
  restore { label: "Service restored", lane: "Resolver group", type: "start-end", color: "green" }
  review { label: "Post-incident review", lane: "Problem management", type: "process", color: "green" }
  detect -> log
  log -> major
  major -> bridge { label: "Yes" }
  major -> resolve { label: "No" }
  bridge -> communicate
  communicate -> resolve
  resolve -> restore
  restore -> review
}
`;

const SERVICE_REQUEST_RDM = `---
type: process
title: "Service request fulfillment"
pool: lane
---
process ServiceRequest {
  request_submitted { label: "Request submitted", lane: "User", type: "start-end", color: "green" }
  catalogue { label: "Match catalogue item", lane: "Service desk", type: "process", color: "blue" }
  approval { label: "Approval required?", lane: "Service owner", type: "decision", color: "amber" }
  approve { label: "Approve request", lane: "Approver", type: "process", color: "green" }
  fulfill { label: "Fulfill request", lane: "Fulfillment team", type: "process", color: "blue" }
  confirm { label: "Confirm with user", lane: "Service desk", type: "process", color: "blue" }
  close_request { label: "Request closed", lane: "User", type: "start-end", color: "green" }
  request_submitted -> catalogue
  catalogue -> approval
  approval -> approve { label: "Yes" }
  approval -> fulfill { label: "No" }
  approve -> fulfill
  fulfill -> confirm
  confirm -> close_request
}
`;

const PROBLEM_MANAGEMENT_RDM = `---
type: process
title: "Problem management"
pool: lane
---
process ProblemManagement {
  trend { label: "Recurring incidents identified", lane: "Service desk", type: "start-end", color: "red" }
  record { label: "Create problem record", lane: "Problem manager", type: "process", color: "blue" }
  analyze { label: "Analyze root cause", lane: "Resolver group", type: "process", color: "amber" }
  known_error { label: "Known error?", lane: "Problem manager", type: "decision", color: "amber" }
  workaround { label: "Publish workaround", lane: "Knowledge owner", type: "process", color: "orange" }
  fix { label: "Raise change for permanent fix", lane: "Change manager", type: "process", color: "blue" }
  verify { label: "Verify incidents reduce", lane: "Problem manager", type: "process", color: "green" }
  close_problem { label: "Problem closed", lane: "Problem manager", type: "start-end", color: "green" }
  trend -> record
  record -> analyze
  analyze -> known_error
  known_error -> workaround { label: "Yes" }
  known_error -> fix { label: "No" }
  workaround -> fix
  fix -> verify
  verify -> close_problem
}
`;

const ACCESS_REVIEW_RDM = `---
type: process
title: "Access review"
pool: lane
---
process AccessReview {
  kickoff { label: "Review period starts", lane: "GRC owner", type: "start-end", color: "green" }
  export { label: "Export access list", lane: "IT admin", type: "process", color: "blue" }
  review { label: "Review entitlements", lane: "Manager", type: "process", color: "blue" }
  exception { label: "Exception found?", lane: "Manager", type: "decision", color: "amber" }
  revoke { label: "Revoke or adjust access", lane: "IT admin", type: "process", color: "red" }
  attest { label: "Attest review complete", lane: "Manager", type: "process", color: "green" }
  evidence { label: "Store evidence", lane: "GRC owner", type: "process", color: "blue" }
  close_review { label: "Control satisfied", lane: "GRC owner", type: "start-end", color: "green" }
  kickoff -> export
  export -> review
  review -> exception
  exception -> revoke { label: "Yes" }
  exception -> attest { label: "No" }
  revoke -> attest
  attest -> evidence
  evidence -> close_review
}
`;

const CONTROL_PROCESS_RDM = `---
type: process
title: "Control process"
pool: lane
---
process ControlProcess {
  trigger { label: "Control trigger", lane: "Control owner", type: "start-end", color: "green" }
  perform { label: "Perform control activity", lane: "Control owner", type: "process", color: "blue" }
  sample { label: "Sample evidence", lane: "Reviewer", type: "process", color: "blue" }
  effective { label: "Effective?", lane: "Reviewer", type: "decision", color: "amber" }
  remediate { label: "Remediate finding", lane: "Control owner", type: "process", color: "red" }
  approve { label: "Approve evidence", lane: "Reviewer", type: "process", color: "green" }
  archive { label: "Archive audit trail", lane: "GRC owner", type: "process", color: "blue" }
  close_control { label: "Ready for audit", lane: "GRC owner", type: "start-end", color: "green" }
  trigger -> perform
  perform -> sample
  sample -> effective
  effective -> approve { label: "Yes" }
  effective -> remediate { label: "No" }
  remediate -> perform
  approve -> archive
  archive -> close_control
}
`;

const EVIDENCE_COLLECTION_RDM = `---
type: process
title: "Evidence collection"
pool: lane
---
process EvidenceCollection {
  request_evidence { label: "Evidence requested", lane: "Auditor / GRC", type: "start-end", color: "green" }
  identify { label: "Identify source system", lane: "Control owner", type: "process", color: "blue" }
  collect { label: "Collect artifact", lane: "Control owner", type: "process", color: "blue" }
  validate { label: "Validate completeness", lane: "GRC owner", type: "decision", color: "amber" }
  refresh { label: "Refresh evidence", lane: "Control owner", type: "process", color: "orange" }
  attach { label: "Attach to control", lane: "GRC owner", type: "process", color: "green" }
  respond { label: "Respond to auditor", lane: "GRC owner", type: "process", color: "blue" }
  close_evidence { label: "Evidence accepted", lane: "Auditor / GRC", type: "start-end", color: "green" }
  request_evidence -> identify
  identify -> collect
  collect -> validate
  validate -> attach { label: "Complete" }
  validate -> refresh { label: "Missing" }
  refresh -> collect
  attach -> respond
  respond -> close_evidence
}
`;

const POLICY_EXCEPTION_RDM = `---
type: process
title: "Policy exception"
pool: lane
---
process PolicyException {
  submit { label: "Exception submitted", lane: "Requester", type: "start-end", color: "green" }
  assess { label: "Assess policy risk", lane: "Risk owner", type: "process", color: "blue" }
  decide { label: "Approve exception?", lane: "Approver", type: "decision", color: "amber" }
  mitigate { label: "Define mitigation", lane: "Risk owner", type: "process", color: "orange" }
  reject { label: "Reject and notify", lane: "Approver", type: "process", color: "red" }
  monitor { label: "Monitor until expiry", lane: "Control owner", type: "process", color: "blue" }
  renew { label: "Renew or close?", lane: "Risk owner", type: "decision", color: "amber" }
  close_exception { label: "Exception closed", lane: "Requester", type: "start-end", color: "green" }
  submit -> assess
  assess -> decide
  decide -> mitigate { label: "Approve" }
  decide -> reject { label: "Reject" }
  mitigate -> monitor
  monitor -> renew
  renew -> close_exception { label: "Close" }
  renew -> assess { label: "Renew" }
  reject -> close_exception
}
`;

const nudgedProcessSeed = (
  id: string,
  label: string,
  rdm: string,
  layoutOptions?: RdmLayoutOptions,
): SeedProvider => ({
  id,
  type: 'flowchart',
  label,
  provide: () => ({
    format: 'commands',
    build: async (cm) => {
      const [{ rdmService }, { ApplyRdmSceneCommand }] = await Promise.all([
        import('../../../domains/rdm/services/RdmService'),
        import('../../commands/ApplyRdmSceneCommand'),
      ]);
      const parsed = rdmService.parseAndValidate(rdm);
      if (!parsed.success || !parsed.document) {
        console.warn('[seed] RDM parse failed', parsed.errors);
        return;
      }
      const { nodes, edges } = await rdmService.toLayoutedRuntimeModel(parsed.document, {
        anchorMode: 'redistribute',
        ...layoutOptions,
      });
      applyProcessTemplateSeedNudges(id, nodes, edges);
      cm.execute(new ApplyRdmSceneCommand({ nodes, edges }));
    },
  }),
});

export const flowchartBasicSeed: SeedProvider = nudgedProcessSeed(
  'flowchart-basic',
  'Basic flowchart',
  FLOWCHART_BASIC_RDM,
  // Keep extra branch breathing room, then apply a small template-specific
  // nudge so "Make changes" clears the "Yes" branch label in the starter.
  { nodeSpacing: 110 },
);

export const swimlaneApprovalSeed: SeedProvider = {
  id: 'swimlane-approval',
  type: 'flowchart',
  label: 'Swimlane — approval',
  provide: () => ({ format: 'rdm', rdm: SWIMLANE_APPROVAL_RDM }),
};

export const bpmnOrderSeed: SeedProvider = {
  id: 'bpmn-order',
  type: 'flowchart',
  label: 'BPMN — order fulfillment',
  provide: () => ({ format: 'rdm', rdm: BPMN_ORDER_RDM }),
};

// Trigger-shaped starters (starter-registry plan R4) — named for the
// urgency moment, not the notation. Same process-RDM mechanism.

export const newHireOnboardingSeed: SeedProvider = {
  id: 'new-hire-onboarding-sop',
  type: 'flowchart',
  label: 'New-hire onboarding SOP',
  provide: () => ({ format: 'rdm', rdm: NEW_HIRE_ONBOARDING_RDM }),
};

export const incidentPostmortemSeed: SeedProvider = {
  id: 'incident-postmortem',
  type: 'flowchart',
  label: 'Incident postmortem',
  provide: () => ({ format: 'rdm', rdm: INCIDENT_POSTMORTEM_RDM }),
};

export const carePathwaySeed: SeedProvider = {
  id: 'care-pathway',
  type: 'flowchart',
  label: 'Care pathway',
  provide: () => ({ format: 'rdm', rdm: CARE_PATHWAY_RDM }),
};

const processSeed = (id: string, label: string, rdm: string): SeedProvider => ({
  id,
  type: 'flowchart',
  label,
  provide: () => ({ format: 'rdm', rdm }),
});

const handoffProcessSeed = nudgedProcessSeed(
  'handoff-process',
  'Team handoff process',
  HANDOFF_PROCESS_RDM,
);

export const additionalProcessTemplateSeeds: SeedProvider[] = [
  processSeed('decision-flow', 'Decision flow', DECISION_FLOW_RDM),
  processSeed('standard-operating-procedure', 'Standard operating procedure', STANDARD_OPERATING_PROCEDURE_RDM),
  handoffProcessSeed,
  processSeed('bpmn-exception-handling', 'BPMN exception handling', BPMN_EXCEPTION_HANDLING_RDM),
  processSeed('bpmn-pool-collaboration', 'BPMN collaboration', BPMN_POOL_COLLABORATION_RDM),
  processSeed('service-blueprint', 'Service blueprint', SERVICE_BLUEPRINT_RDM),
  processSeed('customer-journey-process', 'Customer journey to process', CUSTOMER_JOURNEY_PROCESS_RDM),
  processSeed('support-model', 'Support model', SUPPORT_MODEL_RDM),
  processSeed('sipoc', 'SIPOC', SIPOC_RDM),
  processSeed('value-stream-map', 'Value stream map', VALUE_STREAM_MAP_RDM),
  processSeed('bottleneck-map', 'Bottleneck map', BOTTLENECK_MAP_RDM),
  processSeed('five-whys-root-cause', '5 Whys root cause', FIVE_WHYS_RDM),
  processSeed('change-control', 'Change enablement', CHANGE_CONTROL_RDM),
  processSeed('incident-management', 'Incident management', INCIDENT_MANAGEMENT_RDM),
  processSeed('service-request-fulfillment', 'Service request fulfillment', SERVICE_REQUEST_RDM),
  processSeed('problem-management', 'Problem management', PROBLEM_MANAGEMENT_RDM),
  processSeed('access-review', 'Access review', ACCESS_REVIEW_RDM),
  processSeed('control-process', 'Control process', CONTROL_PROCESS_RDM),
  processSeed('evidence-collection-flow', 'Evidence collection', EVIDENCE_COLLECTION_RDM),
  processSeed('policy-exception-flow', 'Policy exception', POLICY_EXCEPTION_RDM),
];
